import { useCallback, useEffect, useState, type ReactNode } from "react"

import {
  ErroDaApi,
  api,
  baixarRelatorio,
  type ItemOperacional,
  type PainelExecutivo,
  type PainelOperacional,
  type RelatorioDisponivel,
} from "../api"
import { BarraEmPartes, type CorDaParte } from "../componentes/BarraEmPartes"
import { BarrasHorizontais, type ItemDeBarra } from "../componentes/BarrasHorizontais"
import { Abas, PainelDaAba } from "../componentes/Abas"
import { Button } from "../componentes/Button"
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "../componentes/Card"
import { CartaoDeGrafico } from "../componentes/CartaoDeGrafico"
import { ComDica } from "../componentes/ComDica"
import * as Icones from "../componentes/Icones"
import { Numero, Numeros } from "../componentes/Numeros"
import { FalhaNaSecao } from "../componentes/Secao"
import { SITUACOES, Selo, chaveConhecida, chaveDoNivel, type ChaveDeSituacao, type Tom } from "../componentes/Selo"
import { Skeleton } from "../componentes/Skeleton"
import { Column, TablePrincipalFiltroGlobal } from "../componentes/TablePrincipal2"
import { toast } from "../componentes/Toast"
import { ToggleGroup, ToggleGroupItem } from "../componentes/ToggleGroup"
import { COLUNA_NUMERICA, TabelaDaFicha, VazioDaFicha } from "../ficha/Tabela"
import { data, inteiro, maiusculaInicial, moedaCurta, plural, rotulo } from "../formato"
import { MAIUSCULA, MAIUSCULA_INLINE, NOTA, TEXTO_DO_CADASTRO, VAZIO } from "../lib/classes"
import { cn } from "../lib/cn"

const NOME_DO_NIVEL: Record<string, string> = {
  A: "datas expressas",
  B: "prazo e marco expressos",
  C: "regra da entidade",
  D: "data deduzida",
  E: "indeterminado ou em conflito",
  "sem memória": "sem memória de vigência",
}

const NOME_DO_PAPEL: Record<string, string> = {
  nucleo: "instrumento e alterações",
  preparatoria: "fase preparatória",
  autorizacao: "autorização da execução",
  execucao: "execução e pagamento",
  regularidade: "regularidade",
  publicidade: "publicidade",
  alteracao: "ocorrências e encerramento",
}

function mensagem(falha: unknown): string {
  if (falha instanceof ErroDaApi) return falha.acao !== "" ? `${falha.regra}. ${falha.acao}` : falha.regra
  return falha instanceof Error ? falha.message : "falha"
}

const ICONE_DO_AVISO = "mt-0.5 flex shrink-0 text-status-atencao" // anti-ia-ok: token de cor de status, não texto de tela

const ATE_30_DIAS = new Set(["vencidos", "ate_7", "8_15", "16_30"])

function percentual(parte: number, todo: number): number {
  return todo === 0 ? 0 : Math.round((parte / todo) * 100)
}

function itensDe(mapa: Record<string, number>, nomes?: Record<string, string>): ItemDeBarra[] {
  return Object.entries(mapa)
    .sort((a, b) => b[1] - a[1])
    .map(([chave, valor]) => ({ chave, rotulo: maiusculaInicial(nomes?.[chave] ?? rotulo(chave)), valor }))
}

function Distribuicao({
  titulo,
  mapa,
  nomes,
  doQue = "dos contratos",
  atalho,
  classe,
  emDuasColunas = false,
}: {
  titulo: string
  mapa: Record<string, number>
  nomes?: Record<string, string>
  doQue?: string
  atalho?: ReactNode
  classe?: string
  emDuasColunas?: boolean
}) {
  const itens = itensDe(mapa, nomes)
  const total = itens.reduce((soma, item) => soma + item.valor, 0)
  const maior = itens[0]
  return (
    <CartaoDeGrafico
      titulo={titulo}
      direita={`${inteiro(total)} no total`}
      atalho={atalho}
      classe={classe}
      rodape={maior === undefined || total === 0 ? undefined : `${maior.rotulo} representa ${percentual(maior.valor, total)}% ${doQue}`}
    >
      {itens.length === 0 ? <p className={cn(VAZIO, MAIUSCULA)}>Nenhum registro.</p> : <BarrasHorizontais itens={itens} emDuasColunas={emDuasColunas} />}
    </CartaoDeGrafico>
  )
}

function Vencimentos({ executivo, atalho }: { executivo: PainelExecutivo; atalho?: ReactNode }) {
  const [recorte, definirRecorte] = useState<"todos" | "ate_30">("todos")
  const itens: ItemDeBarra[] = executivo.faixas_exclusivas
    .filter((f) => recorte === "todos" || ATE_30_DIAS.has(f.chave))
    .map((f) => ({
      chave: f.chave,
      rotulo: maiusculaInicial(f.rotulo),
      valor: f.contratos,
      texto: plural(f.contratos, "contrato", "contratos"),
      nota: f.valor !== null ? moedaCurta(f.valor) : f.contratos > 0 ? "sem valor" : undefined,
      tom: f.tom === "critico" || f.tom === "atencao" ? f.tom : "informacao",
      selo: <Selo situacao={chaveConhecida(f.tom, "informacao")} semIcone classe="py-1" />,
    }))
  return (
    <CartaoDeGrafico
      titulo="Vencimentos por faixa"
      atalho={atalho}
      classe="lg:col-span-2 lg:row-span-2"
      direita={
        <ToggleGroup
          type="single"
          aria-label="Faixas à vista"
          value={recorte}
          onValueChange={(novo) => novo !== "" && definirRecorte(novo as "todos" | "ate_30")}
        >
          <ToggleGroupItem value="todos">Todos</ToggleGroupItem>
          <ToggleGroupItem value="ate_30">Até 30 dias</ToggleGroupItem>
        </ToggleGroup>
      }
      rodape={
        <>
          <span>
            Total analisado: {plural(executivo.ativos, "contrato", "contratos")}
            {executivo.sem_data > 0 && ` · ${inteiro(executivo.sem_data)} sem data`}
          </span>
          <span>Aviso prévio de continuidade: {inteiro(executivo.faixa_da_continuidade)} dias</span>
        </>
      }
    >
      <BarrasHorizontais itens={itens} />
    </CartaoDeGrafico>
  )
}

const COR_DO_TOM: Record<Tom, CorDaParte> = { verde: "sucesso", ambar: "atencao", vermelho: "erro", cinza: "neutro" }

function SegurancaDaVigencia({ executivo, atalho }: { executivo: PainelExecutivo; atalho?: ReactNode }) {
  const partes = Object.entries(executivo.por_nivel)
    .sort(([a], [b]) => Number(a === "sem memória") - Number(b === "sem memória") || a.localeCompare(b))
    .map(([nivel, contratos]) => ({
      chave: nivel,
      rotulo: maiusculaInicial(NOME_DO_NIVEL[nivel] ?? nivel),
      valor: contratos,
      cor: nivel === "sem memória" ? ("neutro" as const) : COR_DO_TOM[SITUACOES[chaveDoNivel(nivel)].tom],
    }))
  return (
    <CartaoDeGrafico titulo="Segurança da vigência" direita={plural(executivo.ativos, "contrato", "contratos")} atalho={atalho}>
      {partes.length === 0 ? (
        <p className={cn(VAZIO, MAIUSCULA)}>Nenhum registro.</p>
      ) : (
        <BarraEmPartes partes={partes} rotulo="Segurança da vigência" />
      )}
    </CartaoDeGrafico>
  )
}

type Categoria = "modalidade" | "tipo" | "situacao" | "secretaria"

function DistribuicaoDosContratos({ executivo }: { executivo: PainelExecutivo }) {
  const categorias: { chave: Categoria; rotulo: string; mapa: Record<string, number> }[] = [
    { chave: "modalidade", rotulo: "Modalidade", mapa: executivo.por_modalidade },
    { chave: "tipo", rotulo: "Tipo de objeto", mapa: executivo.por_tipo },
    ...(Object.keys(executivo.por_situacao).length > 1
      ? [{ chave: "situacao" as const, rotulo: "Situação", mapa: executivo.por_situacao }]
      : []),
    ...(Object.keys(executivo.por_entidade).length > 1
      ? [{ chave: "secretaria" as const, rotulo: "Secretaria", mapa: executivo.por_entidade }]
      : []),
  ]
  const [escolhida, definirEscolhida] = useState<Categoria>("modalidade")
  const atual = categorias.find((c) => c.chave === escolhida) ?? categorias[0]
  const itens = atual === undefined ? [] : itensDe(atual.mapa)
  const total = itens.reduce((soma, item) => soma + item.valor, 0)
  const maior = itens[0]
  return (
    <CartaoDeGrafico
      titulo="Distribuição dos contratos"
      direita={`${inteiro(total)} no total`}
      rodape={maior === undefined || total === 0 ? undefined : `${maior.rotulo} representa ${percentual(maior.valor, total)}% dos contratos`}
    >
      <div className="flex flex-col gap-4">
        <ToggleGroup
          type="single"
          aria-label="Distribuir os contratos por"
          className="flex-wrap"
          value={atual?.chave ?? ""}
          onValueChange={(nova) => nova !== "" && definirEscolhida(nova as Categoria)}
        >
          {categorias.map((c) => (
            <ToggleGroupItem key={c.chave} value={c.chave}>
              {c.rotulo}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        {itens.length === 0 ? <p className={cn(VAZIO, MAIUSCULA)}>Nenhum registro.</p> : <BarrasHorizontais itens={itens} />}
      </div>
    </CartaoDeGrafico>
  )
}

function FaixaDeCanais({ executivo, aoAbrirCanaisDeAviso }: { executivo: PainelExecutivo; aoAbrirCanaisDeAviso?: () => void }) {
  const parados = Object.values(executivo.avisos_30_dias).reduce((soma, n) => soma + n, 0)
  return (
    <div
      data-slot="faixa-de-canais"
      role="status"
      className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-md border bg-card px-4 py-3 text-sm"
    >
      <p className="m-0 flex min-w-0 flex-1 basis-80 items-start gap-2">
        <span className={ICONE_DO_AVISO}>
          <Icones.Ressalva tamanho={16} />
        </span>
        <span>
          E-mail e WhatsApp não configurados: nos últimos 30 dias, {plural(parados, "aviso ficou", "avisos ficaram")} só na caixa do sistema
        </span>
      </p>
      {aoAbrirCanaisDeAviso !== undefined && (
        <Button type="button" variant="outline" size="sm" onClick={aoAbrirCanaisDeAviso}>
          <Icones.Canal />
          Abrir Canais de aviso
        </Button>
      )}
    </div>
  )
}

function AvisosPorCanal({ executivo, atalho }: { executivo: PainelExecutivo; atalho?: ReactNode }) {
  const itens = itensDe(executivo.avisos_30_dias).map((item) => ({
    ...item,
    tom: item.chave.includes("falha") ? ("critico" as const) : undefined,
  }))
  const total = itens.reduce((soma, item) => soma + item.valor, 0)
  return (
    <CartaoDeGrafico titulo="Avisos por e-mail e WhatsApp nos últimos 30 dias" direita={`${inteiro(total)} no total`} atalho={atalho}>
      {itens.length === 0 ? <p className={cn(VAZIO, MAIUSCULA)}>Nenhum aviso enviado.</p> : <BarrasHorizontais itens={itens} />}
    </CartaoDeGrafico>
  )
}

function EsqueletoDoCenario() {
  return (
    <div aria-busy="true" className="flex flex-col gap-2">
      <Card>
        <CardContent className="flex flex-col gap-4">
          <Skeleton className="h-4 w-48" />
          <div className="grid grid-cols-[repeat(auto-fit,minmax(min(10.5rem,100%),1fr))] gap-3">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
      <div className="grid grid-cols-1 gap-2 lg:grid-cols-3">
        <Skeleton className="h-64 w-full lg:col-span-2" />
        <Skeleton className="h-64 w-full" />
      </div>
    </div>
  )
}

export function Relatorios({
  aoAbrirContrato,
  aoAbrirCanaisDeAviso,
}: {
  aoAbrirContrato: (id: number) => void
  aoAbrirCanaisDeAviso?: () => void
}) {
  const [executivo, definirExecutivo] = useState<PainelExecutivo | null>(null)
  const [operacional, definirOperacional] = useState<PainelOperacional | null>(null)
  const [relatorios, definirRelatorios] = useState<RelatorioDisponivel[]>([])
  const [erro, definirErro] = useState<string | null>(null)
  const [baixando, definirBaixando] = useState<string | null>(null)

  const carregar = useCallback(() => {
    definirErro(null)
    definirExecutivo(null)
    Promise.all([api.painelExecutivo(), api.painelOperacional(), api.relatorios()])
      .then(([e, o, r]) => {
        definirExecutivo(e)
        definirOperacional(o)
        definirRelatorios(r)
      })
      .catch((f: unknown) => definirErro(mensagem(f)))
  }, [])

  useEffect(() => {
    carregar()
  }, [carregar])

  const baixar = async (nome: string) => {
    definirBaixando(nome)
    try {
      await baixarRelatorio(nome)
    } catch (f: unknown) {
      toast.error(`Não foi possível baixar o relatório: ${mensagem(f)}`)
    } finally {
      definirBaixando(null)
    }
  }

  if (erro !== null) {
    return (
      <Card>
        <CardContent>
          <FalhaNaSecao mensagem={`Não foi possível carregar: ${erro}`} aoTentar={carregar} />
        </CardContent>
      </Card>
    )
  }
  if (executivo === null || operacional === null) {
    return <EsqueletoDoCenario />
  }

  const atalho = (nome: string, dica: string) =>
    relatorios.some((r) => r.nome === nome) ? (
      <Button
        type="button"
        variant="outline"
        size="icon-sm"
        title={dica}
        aria-label={dica}
        disabled={baixando === nome}
        onClick={() => void baixar(nome)}
      >
        <Icones.Baixar />
      </Button>
    ) : undefined

  const atrasadas = executivo.providencias.atrasadas ?? 0
  const atraso = executivo.atraso_das_providencias
  const notaDosAtivos = [
    moedaCurta(executivo.ativos_valor),
    executivo.ativos_sem_valor > 0 ? `${inteiro(executivo.ativos_sem_valor)} sem valor` : null,
    `${inteiro(executivo.novos_no_mes)} este mês`,
    executivo.sem_data_de_inicio > 0 ? `${inteiro(executivo.sem_data_de_inicio)} sem data de início` : null,
  ]
    .filter((parte) => parte !== null)
    .join(" · ")

  return (
    <>
      <section data-slot="cenario-executivo" className="flex flex-col gap-2">
        <Card>
          <CardHeader>
            <CardTitle>Cenário executivo</CardTitle>
            <CardAction>
              <span className={cn(NOTA, MAIUSCULA_INLINE)}>datas de referência: a memória de vigência de cada contrato</span>
            </CardAction>
          </CardHeader>
          <CardContent>
            <Numeros>
              <Numero rotulo="Contratos ativos" valor={inteiro(executivo.ativos)} nota={notaDosAtivos} />
              <Numero
                rotulo="Vencidos em execução"
                valor={inteiro(executivo.vencidos_em_execucao)}
                nota={moedaCurta(executivo.vencidos_valor)}
                tom={executivo.vencidos_em_execucao > 0 ? "critico" : undefined}
              />
              <Numero rotulo="Sem definição segura de vigência" valor={inteiro(executivo.sem_definicao_segura)} nota={`${inteiro(executivo.sem_data_nenhuma)} sem data nenhuma`} />
              <Numero rotulo="Sem providência de continuidade" valor={inteiro(executivo.sem_providencia_de_continuidade)} nota={`Vencendo em até ${inteiro(executivo.faixa_da_continuidade)} dias`} />
              <Numero rotulo="Valor exposto a risco" valor={moedaCurta(executivo.valor_exposto_a_risco)} nota="Vence em 90 dias sem providência concluída" />
              <Numero
                rotulo="Providências atrasadas"
                valor={inteiro(atrasadas)}
                tom={atrasadas > 0 ? "critico" : undefined}
                nota={
                  atraso.medio_dias !== null && atraso.maior_dias !== null
                    ? `Atraso médio de ${plural(atraso.medio_dias, "dia", "dias")} · a mais atrasada, ${plural(atraso.maior_dias, "dia", "dias")}`
                    : `${inteiro(executivo.providencias.pendente ?? 0)} pendentes · ${inteiro(executivo.providencias.concluida ?? 0)} concluídas`
                }
              />
            </Numeros>
          </CardContent>
        </Card>
        {!executivo.canais_configurados && <FaixaDeCanais executivo={executivo} aoAbrirCanaisDeAviso={aoAbrirCanaisDeAviso} />}
        <div data-slot="grade-do-cenario" className="grid grid-cols-1 gap-2 lg:grid-cols-3">
          <Vencimentos executivo={executivo} atalho={atalho("vencimentos", "Baixar CSV de vencimentos")} />
          <SegurancaDaVigencia executivo={executivo} atalho={atalho("memoria-de-vigencia", "Baixar CSV da memória de vigência")} />
          <DistribuicaoDosContratos executivo={executivo} />
          <Distribuicao
            titulo="Peças do processo, por papel"
            mapa={executivo.pecas_por_papel}
            nomes={NOME_DO_PAPEL}
            doQue="das peças"
            atalho={atalho("pecas", "Baixar CSV das peças")}
            classe={executivo.canais_configurados ? "lg:col-span-2" : "lg:col-span-3"}
            emDuasColunas={!executivo.canais_configurados}
          />
          {executivo.canais_configurados && <AvisosPorCanal executivo={executivo} atalho={atalho("avisos", "Baixar CSV dos avisos")} />}
        </div>
      </section>

      <CriticosPorValor criticos={executivo.criticos_por_valor} aoAbrirContrato={aoAbrirContrato} />

      <MinhaFila operacional={operacional} aoAbrirContrato={aoAbrirContrato} />

      <Card>
        <CardHeader>
          <CardTitle>Relatórios para instrução e auditoria</CardTitle>
          <CardAction>
            <span className={cn(NOTA, MAIUSCULA_INLINE)}>Planilha CSV (abre no Excel)</span>
          </CardAction>
        </CardHeader>
        <CardContent>
          <ul data-slot="lista-de-relatorios" className="m-0 flex list-none flex-col divide-y divide-border p-0">
            {relatorios.map((r) => (
              <li key={r.nome} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 py-3">
                <span className="flex min-w-0 flex-1 basis-60 flex-col gap-0.5">
                  <span className="text-sm font-medium text-foreground">{r.titulo}</span>
                  <span className={cn("text-sm text-muted-foreground", MAIUSCULA)}>{r.descricao}</span>
                </span>
                <Button variant="outline" size="sm" type="button" disabled={baixando === r.nome} onClick={() => void baixar(r.nome)}>
                  <Icones.Baixar />
                  {baixando === r.nome ? "Gerando..." : "Baixar CSV"}
                </Button>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </>
  )
}

type Gravidade = Extract<ChaveDeSituacao, "critico" | "atencao" | "informacao">

function SeloSemIcone({ situacao, titulo, children }: { situacao: ChaveDeSituacao; titulo?: string; children: ReactNode }) {
  return (
    <Selo situacao={situacao} titulo={titulo} semIcone classe="py-1">
      {children}
    </Selo>
  )
}

function prazoEmPalavras(dias: number, passou: string): string {
  if (dias < 0) return `${passou} há ${plural(-dias, "dia", "dias")}`
  if (dias === 0) return "Vence hoje"
  return `Em ${plural(dias, "dia", "dias")}`
}

function gravidadeDoVencimento(dias: number): Gravidade {
  return dias <= 30 ? "critico" : "atencao"
}

function gravidadeDaFila(dias: number): Gravidade {
  return dias < 0 ? "critico" : dias <= 7 ? "atencao" : "informacao"
}

const DICA_DA_PREVENTIVA = "A data jurídica ainda não existe: o prazo conta da data preventiva"

function Preventiva() {
  return (
    <SeloSemIcone situacao="informacao" titulo={DICA_DA_PREVENTIVA}>
      Preventiva
    </SeloSemIcone>
  )
}

function LinkDoContrato({ numero, aoAbrir }: { numero: string; aoAbrir: () => void }) {
  return (
    <button
      type="button"
      className="cursor-pointer font-semibold whitespace-nowrap text-primary hover:underline"
      onClick={(evento) => {
        evento.stopPropagation()
        aoAbrir()
      }}
    >
      {numero}
    </button>
  )
}

type Critico = PainelExecutivo["criticos_por_valor"][number]

function ordemDoNumero(numero: string): number {
  const [n, ano] = numero.split("/").map(Number)
  return (ano ?? 0) * 100000 + (n ?? 0)
}

const ORDEM_DOS_CRITICOS: Record<string, (c: Critico) => string | number | null> = {
  numero: (c) => ordemDoNumero(c.numero),
  valor: (c) => (c.valor === null ? null : Number(c.valor)),
  fim: (c) => c.dias,
}

function CriticosPorValor({ criticos, aoAbrirContrato }: { criticos: Critico[]; aoAbrirContrato: (id: number) => void }) {
  const [busca, definirBusca] = useState("")
  const termo = busca.trim().toLowerCase()
  const visiveis = termo === "" ? criticos : criticos.filter((c) => `${c.numero} ${c.objeto}`.toLowerCase().includes(termo))
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-1.5">
          Contratos críticos por valor
          <ComDica dica="Vencem em até 90 dias, do maior valor para o menor" />
        </CardTitle>
      </CardHeader>
      <CardContent>
        <TabelaDaFicha<Critico>
          dados={visiveis}
          ordenar={ORDEM_DOS_CRITICOS}
          aoClicarNaLinha={(c) => aoAbrirContrato(c.contrato_id)}
          filtrosNaBarra={
            <div className="flex w-full md:w-[364px]">
              <TablePrincipalFiltroGlobal globalFilter={busca} setGlobalFilter={definirBusca} placeholder="Número ou objeto" />
            </div>
          }
          vazio={<VazioDaFicha titulo={termo === "" ? "Nenhum contrato com valor vencendo em 90 dias" : "Nenhum contrato com esta pesquisa"} />}
        >
          <Column<Critico>
            name="numero"
            label="Contrato"
            render={(_v, c) => <LinkDoContrato numero={c.numero} aoAbrir={() => aoAbrirContrato(c.contrato_id)} />}
          />
          <Column<Critico> name="objeto" label="Objeto" classe="min-w-[240px]" render={(_v, c) => <span className={TEXTO_DO_CADASTRO}>{c.objeto}</span>} />
          <Column<Critico>
            name="valor"
            label="Valor"
            classe={COLUNA_NUMERICA}
            render={(_v, c) => <span className="whitespace-nowrap tabular-nums">{moedaCurta(c.valor)}</span>}
          />
          <Column<Critico>
            name="fim"
            label="Término"
            render={(_v, c) => (
              <span className="flex flex-wrap items-center gap-1.5">
                <span className="flex items-center gap-1.5 whitespace-nowrap">
                  <span className="tabular-nums">{data(c.fim)}</span>
                  {c.dias !== null && (
                    <SeloSemIcone situacao={gravidadeDoVencimento(c.dias)}>{prazoEmPalavras(c.dias, "Vencido")}</SeloSemIcone>
                  )}
                </span>
                {c.preventiva && <Preventiva />}
              </span>
            )}
          />
          <Column<Critico>
            name="nivel"
            label="Segurança da vigência"
            render={(_v, c) =>
              c.nivel === null ? (
                <span className="text-muted-foreground">—</span>
              ) : (
                <SeloSemIcone situacao={chaveDoNivel(c.nivel)}>{maiusculaInicial(NOME_DO_NIVEL[c.nivel] ?? c.nivel)}</SeloSemIcone>
              )
            }
          />
        </TabelaDaFicha>
      </CardContent>
    </Card>
  )
}

type ChaveDaFila = "prazos_atrasados" | "providencias_hoje" | "aguardando_manifestacao" | "conflitos_documentais" | "documentos_a_vencer"

const ABAS_DA_FILA: { chave: ChaveDaFila; rotulo: string; providencia: boolean; passou: string }[] = [
  { chave: "prazos_atrasados", rotulo: "Prazos atrasados", providencia: true, passou: "Atrasado" },
  { chave: "providencias_hoje", rotulo: "Próximos 7 dias", providencia: true, passou: "Atrasado" },
  { chave: "aguardando_manifestacao", rotulo: "Aguardando manifestação", providencia: false, passou: "Vencido" },
  { chave: "conflitos_documentais", rotulo: "Conflitos documentais", providencia: false, passou: "Vencido" },
  { chave: "documentos_a_vencer", rotulo: "Documentos e garantias", providencia: false, passou: "Vencido" },
]

const ORDEM_DA_FILA: Record<string, (i: ItemOperacional) => number | null> = {
  contrato_numero: (i) => ordemDoNumero(i.contrato_numero),
  prazo: (i) => i.dias,
}

function MinhaFila({ operacional, aoAbrirContrato }: { operacional: PainelOperacional; aoAbrirContrato: (id: number) => void }) {
  const [aba, definirAba] = useState<ChaveDaFila>(
    () => ABAS_DA_FILA.find((a) => operacional[a.chave].length > 0)?.chave ?? "prazos_atrasados",
  )
  return (
    <Card>
      <CardHeader>
        <CardTitle>Minha fila de hoje</CardTitle>
        <CardAction>
          <span className={cn(NOTA, MAIUSCULA_INLINE)}>
            {plural(operacional.meus_contratos, "contrato", "contratos")} sob minha responsabilidade
          </span>
        </CardAction>
      </CardHeader>
      <CardContent>
        <Abas
          nome="fila"
          rotulo="Minha fila de hoje"
          abas={ABAS_DA_FILA.map((a) => ({ chave: a.chave, rotulo: a.rotulo, contagem: operacional[a.chave].length }))}
          atual={aba}
          aoTrocar={definirAba}
        />
        {ABAS_DA_FILA.map((a) => (
          <PainelDaAba key={a.chave} nome="fila" chave={a.chave} ativa={aba === a.chave}>
            {aba === a.chave && (
              <TabelaDaFicha<ItemOperacional>
                dados={operacional[a.chave]}
                ordenar={ORDEM_DA_FILA}
                aoClicarNaLinha={(i) => aoAbrirContrato(i.contrato_id)}
                vazio={<VazioDaFicha titulo={`Nada em ${a.rotulo.toLowerCase()}`} />}
              >
                <Column<ItemOperacional>
                  name="contrato_numero"
                  label="Contrato"
                  render={(_v, i) => <LinkDoContrato numero={i.contrato_numero} aoAbrir={() => aoAbrirContrato(i.contrato_id)} />}
                />
                <Column<ItemOperacional>
                  name="o_que"
                  label={a.providencia ? "Providência" : "Pendência"}
                  classe="min-w-[260px]"
                  render={(_v, i) => (
                    <span className="flex flex-col gap-0.5">
                      <span className={cn("text-foreground", MAIUSCULA)}>{i.o_que}</span>
                      {i.detalhe !== null && <span className={cn("text-xs text-muted-foreground", MAIUSCULA)}>{i.detalhe}</span>}
                      <span className={cn("text-xs text-muted-foreground", TEXTO_DO_CADASTRO)}>{i.contrato_objeto}</span>
                    </span>
                  )}
                />
                {a.providencia && (
                  <Column<ItemOperacional>
                    name="responsavel"
                    label="Responsável"
                    render={(_v, i) => (
                      <span className="flex flex-wrap items-center gap-1.5">
                        {i.responsavel !== null && (
                          <SeloSemIcone situacao="informacao" titulo="Quem responde pela providência">
                            {i.responsavel}
                          </SeloSemIcone>
                        )}
                        {i.preventiva && <Preventiva />}
                      </span>
                    )}
                  />
                )}
                <Column<ItemOperacional>
                  name="prazo"
                  label="Prazo"
                  render={(_v, i) => (
                    <span className="flex items-center gap-1.5 whitespace-nowrap">
                      <span className="tabular-nums">{data(i.prazo)}</span>
                      {i.dias !== null && (
                        <SeloSemIcone situacao={gravidadeDaFila(i.dias)}>{prazoEmPalavras(i.dias, a.passou)}</SeloSemIcone>
                      )}
                    </span>
                  )}
                />
              </TabelaDaFicha>
            )}
          </PainelDaAba>
        ))}
      </CardContent>
    </Card>
  )
}
